/**
 * A COPY of the chat service's pageMarkdown.ts (avengents-homepage chat/lambda), kept byte-for-byte below this note so
 * the two read and write the same Markdown — pinned by the same tests (markdown.test.ts). Here it lets a sticky note on
 * the whiteboard hold Markdown (owner, 2026-11-02: 讓便條貼顯示 Markdown 文本, 並且能夠預覽編輯 Markdown): the board draws it
 * (board/noteText.ts) and the note's editor writes it (board/NoteEditor.tsx).
 *
 * A PAGE AS MARKDOWN — how an agent reads and writes a project's page (internalPage.ts; owner, 2026-11-02: 之後我們可以
 * 有自己的頁面功能 (還能夠跟 Agents 共同編輯 …)). People edit a page as blocks (BlockNote, in the app); an agent reads
 * and writes text, and the text every model already writes is Markdown.
 *
 * THE DIALECT is CommonMark's everyday part, plus the few tags of Notion's own Markdown for agents (its "Notion-flavored
 * Markdown") where Markdown has no word — so an agent that knows one knows the other:
 *
 *   paragraph        words; a line break inside it is a new line (an empty line in it: <br>)
 *   empty paragraph  <p />
 *   heading          # … ###### (a toggle heading: <toggle heading="h2">)
 *   lists            - item   1. item   - [ ] to-do   - [x] done   (what is inside an item: indented under it)
 *   toggle           <toggle>Title</toggle>, or, with what it holds:  <toggle>⏎Title⏎⏎…blocks…⏎</toggle>
 *   quote            > words
 *   code             ```lang … ```
 *   table            a pipe table (its first row is the header)
 *   divider          ---
 *   words            **bold** *italic* ~~strike~~ `code` [link](url) <u>underline</u> <span color="red">…</span>
 *                    (<span color="red_bg"> a background) — and <b> <i> <s> where the stars cannot say it
 *
 * Any other block's children are indented under it (four spaces). What Markdown cannot say of a block (its colour, its
 * alignment) is not lost: a block the agent leaves as it read it is never written (pageMerge.ts compares the text), and
 * one it changes keeps them.
 *
 * Pure. Rendering checks itself: a block's words are written with Markdown's marks when they read back the same, else
 * with tags (which always do).
 */

// ── the shapes ──────────────────────────────────────────────────────────────────────────────────────────────────

export type Styles = Record<string, unknown>;
export type TextRun = { type: "text"; text: string; styles: Styles };
export type LinkRun = { type: "link"; href: string; content: TextRun[] };
export type Inline = TextRun | LinkRun;

/** A block as the editor keeps it (the row's type/props/content), with its children. */
export type Block = {
  id?: string;
  type: string;
  props?: Record<string, unknown>;
  content?: unknown;
  children: Block[];
};

/** A block read from Markdown: also the lines it was written as (its own, not its children's). */
export type Parsed = Block & { raw: string; children: Parsed[] };

/** The list items: consecutive ones sit on consecutive lines, and their children are indented under them. */
const LIST = new Set(["bulletListItem", "numberedListItem", "checkListItem"]);

const STYLE_KEYS = ["bold", "italic", "underline", "strike", "code"] as const;
const COLOR_RE = /^[a-z]+$/;

// ── words: reading ──────────────────────────────────────────────────────────────────────────────────────────────

type Atom =
  | { k: "text"; text: string; s: Styles }
  | { k: "link"; href: string; runs: TextRun[]; s: Styles }
  | { k: "delim"; ch: string; n: number; open: boolean; close: boolean; s: Styles };

const PUNCT = /[\p{P}\p{S}]/u;
const SPACE = /\s/u;
const ESCAPABLE = /[!-/:-@[-`{-~]/;

function flank(prev: string, next: string) {
  const prevSpace = !prev || SPACE.test(prev);
  const nextSpace = !next || SPACE.test(next);
  const prevPunct = !!prev && PUNCT.test(prev);
  const nextPunct = !!next && PUNCT.test(next);
  const left = !nextSpace && (!nextPunct || prevSpace || prevPunct);
  const right = !prevSpace && (!prevPunct || nextSpace || nextPunct);
  return { left, right, prevPunct, nextPunct };
}

/** The `]` that closes the `[` at `i`, honouring escapes, code and nesting; -1 when there is none. */
function closeBracket(s: string, i: number): number {
  let depth = 0;
  for (let j = i; j < s.length; j++) {
    const c = s[j]!;
    if (c === "\\") {
      j++;
      continue;
    }
    if (c === "`") {
      let n = 0;
      while (s[j + n] === "`") n++;
      const end = s.indexOf("`".repeat(n), j + n);
      if (end >= 0 && s[end + n] !== "`") j = end + n - 1;
      else j += n - 1;
      continue;
    }
    if (c === "[") depth++;
    if (c === "]" && --depth === 0) return j;
  }
  return -1;
}

/** `(dest "title")` after a link's text, from `i` (at the `(`): the destination and where it ends. */
function linkDest(s: string, i: number): { href: string; end: number } | null {
  if (s[i] !== "(") return null;
  let j = i + 1;
  while (s[j] === " ") j++;
  let href = "";
  if (s[j] === "<") {
    const close = s.indexOf(">", j);
    if (close < 0 || s.slice(j + 1, close).includes("\n")) return null;
    href = s.slice(j + 1, close);
    j = close + 1;
  } else {
    let depth = 0;
    const from = j;
    for (; j < s.length; j++) {
      const c = s[j]!;
      if (c === "\\" && j + 1 < s.length) {
        j++;
        continue;
      }
      if (SPACE.test(c)) break;
      if (c === "(") depth++;
      if (c === ")") {
        if (depth === 0) break;
        depth--;
      }
    }
    href = s.slice(from, j).replace(/\\([!-/:-@[-`{-~])/g, "$1");
  }
  while (s[j] === " ") j++;
  if (s[j] === '"' || s[j] === "'") {
    const q = s[j]!;
    const close = s.indexOf(q, j + 1);
    if (close < 0) return null;
    j = close + 1;
    while (s[j] === " ") j++;
  }
  if (s[j] !== ")") return null;
  return { href, end: j + 1 };
}

const TAG_RE = /^<(\/?)(b|strong|i|em|s|del|strike|u|span)((?:\s+[a-z-]+="[^"]*")*)\s*>/i;
const TAG_STYLE: Record<string, string> = { b: "bold", strong: "bold", i: "italic", em: "italic", s: "strike", del: "strike", strike: "strike", u: "underline" };

/**
 * Markdown's words into the editor's runs. CommonMark's emphasis (its delimiter rules — `*` and `_`, and GFM's `~~`),
 * code spans, links (`[t](url)`, `<https://…>`), backslash escapes, and the tags the dialect names. A bare URL stays
 * words, as a person typed them. Anything else is said as written.
 */
export function parseInline(src: string): Inline[] {
  const atoms: Atom[] = [];
  const tagCount: Record<string, number> = {};
  const colors: Array<{ key: string; value: string } | null> = [];
  const tagStyles = (): Styles => {
    const s: Styles = {};
    for (const [k, n] of Object.entries(tagCount)) if (n > 0) s[k] = true;
    for (const c of colors) if (c) s[c.key] = c.value;
    return s;
  };
  const pushText = (text: string, extra?: Styles) => {
    const s = { ...tagStyles(), ...(extra ?? {}) };
    const last = atoms[atoms.length - 1];
    if (last && last.k === "text" && sameStyles(last.s, s)) last.text += text;
    else atoms.push({ k: "text", text, s });
  };

  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === "\\" && i + 1 < src.length && ESCAPABLE.test(src[i + 1]!)) {
      pushText(src[i + 1]!);
      i += 2;
      continue;
    }
    if (c === "`") {
      let n = 0;
      while (src[i + n] === "`") n++;
      let at = i + n;
      let end = -1;
      while ((at = src.indexOf("`", at)) >= 0) {
        let m = 0;
        while (src[at + m] === "`") m++;
        if (m === n) {
          end = at;
          break;
        }
        at += m;
      }
      if (end < 0) {
        pushText("`".repeat(n));
        i += n;
        continue;
      }
      let code = src.slice(i + n, end);
      if (code.length >= 2 && code.startsWith(" ") && code.endsWith(" ") && code.trim()) code = code.slice(1, -1);
      atoms.push({ k: "text", text: code, s: { ...tagStyles(), code: true } });
      i = end + n;
      continue;
    }
    if (c === "[" || (c === "!" && src[i + 1] === "[")) {
      const open = c === "!" ? i + 1 : i;
      const close = closeBracket(src, open);
      const dest = close >= 0 ? linkDest(src, close + 1) : null;
      if (dest) {
        const runs = flatRuns(parseInline(src.slice(open + 1, close)));
        atoms.push({ k: "link", href: dest.href, runs, s: tagStyles() });
        i = dest.end;
        continue;
      }
      pushText(c);
      i++;
      continue;
    }
    if (c === "<") {
      const rest = src.slice(i);
      const br = /^<br\s*\/?>/i.exec(rest);
      if (br) {
        pushText("\n");
        i += br[0].length;
        // A <br> at a line's end is that line's break: the newline after it is not another.
        if (src[i] === "\n") i++;
        continue;
      }
      const auto = /^<((?:https?|mailto):[^\s<>]+)>/i.exec(rest);
      if (auto) {
        atoms.push({ k: "link", href: auto[1]!, runs: [{ type: "text", text: auto[1]!, styles: {} }], s: tagStyles() });
        i += auto[0].length;
        continue;
      }
      const tag = TAG_RE.exec(rest);
      if (tag) {
        const closing = tag[1] === "/";
        const name = tag[2]!.toLowerCase();
        if (name === "span") {
          if (closing) colors.pop();
          else {
            const m = /\scolor="([a-z_]+)"/i.exec(tag[3] ?? "");
            const v = m?.[1]?.toLowerCase() ?? "";
            const bg = v.endsWith("_bg");
            const value = bg ? v.slice(0, -3) : v;
            colors.push(value && COLOR_RE.test(value) && value !== "default" ? { key: bg ? "backgroundColor" : "textColor", value } : null);
          }
        } else {
          const key = TAG_STYLE[name]!;
          tagCount[key] = Math.max(0, (tagCount[key] ?? 0) + (closing ? -1 : 1));
        }
        i += tag[0].length;
        continue;
      }
      pushText("<");
      i++;
      continue;
    }
    if (c === "*" || c === "_" || c === "~") {
      let n = 0;
      while (src[i + n] === c) n++;
      if (c === "~" && n !== 2) {
        pushText("~".repeat(n));
        i += n;
        continue;
      }
      const f = flank(src[i - 1] ?? "", src[i + n] ?? "");
      const open = c === "_" ? f.left && (!f.right || f.prevPunct) : f.left;
      const close = c === "_" ? f.right && (!f.left || f.nextPunct) : f.right;
      atoms.push({ k: "delim", ch: c, n, open, close, s: tagStyles() });
      i += n;
      continue;
    }
    pushText(c);
    i++;
  }

  emphasis(atoms);

  const out: Inline[] = [];
  for (const a of atoms) {
    if (a.k === "link") {
      const runs = a.runs.map((r) => ({ type: "text" as const, text: r.text, styles: linkStyles({ ...r.styles, ...a.s }) }));
      out.push({ type: "link", href: a.href, content: mergeRuns(runs) as TextRun[] });
    } else if (a.k === "text") out.push({ type: "text", text: a.text, styles: clean(a.s) });
    else if (a.n > 0) out.push({ type: "text", text: a.ch.repeat(a.n), styles: clean(a.s) });
  }
  return mergeRuns(out);
}

/** CommonMark's "process emphasis": each closer finds its opener; what lies between takes the style. */
function emphasis(atoms: Atom[]): void {
  for (let ci = 0; ci < atoms.length; ci++) {
    const closer = atoms[ci]!;
    if (closer.k !== "delim" || !closer.close || closer.n === 0) continue;
    let oi = ci - 1;
    for (; oi >= 0; oi--) {
      const o = atoms[oi]!;
      if (o.k !== "delim" || o.ch !== closer.ch || !o.open || o.n === 0) continue;
      if (closer.ch === "~") {
        if (o.n !== closer.n) continue;
        break;
      }
      // The rule of 3.
      if ((o.close || closer.open) && (o.n + closer.n) % 3 === 0 && !(o.n % 3 === 0 && closer.n % 3 === 0)) continue;
      break;
    }
    if (oi < 0) continue;
    const opener = atoms[oi] as Extract<Atom, { k: "delim" }>;
    const use = closer.ch === "~" ? 2 : opener.n >= 2 && closer.n >= 2 ? 2 : 1;
    const style = closer.ch === "~" ? "strike" : use === 2 ? "bold" : "italic";
    for (let k = oi + 1; k < ci; k++) {
      const a = atoms[k]!;
      // Delimiters left between them are words now.
      if (a.k === "delim") {
        a.open = false;
        a.close = false;
      }
      a.s = { ...a.s, [style]: true };
    }
    opener.n -= use;
    closer.n -= use;
    if (closer.n > 0) ci--;
  }
}

/**
 * A run's styles as the editor can hold them. BlockNote's `code` excludes every other mark — bold, italic, a colour, a
 * link too — and a page holding one run of code AND bold cannot be opened at all (found on prod 2026-11-02: an agent's
 * `**with the sidecar `.json`**` took a room's page down for every reader). So code words are code alone; inside a link
 * (linkStyles) they are the link's words.
 */
function clean(s: Styles): Styles {
  if (s["code"] === true) return { code: true };
  const out: Styles = {};
  for (const k of STYLE_KEYS) if (s[k] === true) out[k] = true;
  for (const k of ["textColor", "backgroundColor"]) {
    const v = s[k];
    if (typeof v === "string" && v && v !== "default") out[k] = v;
  }
  return out;
}

/** A link's words: no code among them (BlockNote's code mark excludes the link). */
const linkStyles = (s: Styles): Styles => {
  const { code: _code, ...rest } = clean(s);
  return rest;
};

function sameStyles(a: Styles, b: Styles): boolean {
  const ka = Object.keys(a).filter((k) => a[k] !== undefined && a[k] !== false);
  const kb = Object.keys(b).filter((k) => b[k] !== undefined && b[k] !== false);
  return ka.length === kb.length && ka.every((k) => a[k] === b[k]);
}

/** Runs as the editor keeps them: no empty words, neighbours of one style joined — and of one link (the editor's link
 *  is a mark: two touching links to one address are one). */
function mergeRuns(runs: Inline[]): Inline[] {
  const out: Inline[] = [];
  for (const r of runs.flatMap(breaksApart)) {
    if (r.type === "link") {
      const last = out[out.length - 1];
      if (last && last.type === "link" && last.href === r.href) {
        out[out.length - 1] = { ...last, content: mergeRuns([...last.content, ...r.content]) as TextRun[] };
        continue;
      }
      if (!r.content.some((c) => c.text)) continue;
    }
    if (r.type === "text") {
      if (!r.text) continue;
      const last = out[out.length - 1];
      if (last && last.type === "text" && sameStyles(last.styles, r.styles)) {
        out[out.length - 1] = { ...last, text: last.text + r.text };
        continue;
      }
    }
    out.push(r);
  }
  return out;
}

/** A line break shows no code mark, and code is said a line at a time: a break inside code words is never code. */
function breaksApart(r: Inline): Inline[] {
  if (r.type !== "text" || !r.styles["code"] || !r.text.includes("\n")) return [r];
  const { code: _code, ...plain } = r.styles;
  return r.text
    .split(/(\n+)/)
    .filter(Boolean)
    .map((text) => ({ type: "text" as const, text, styles: text.startsWith("\n") ? plain : r.styles }));
}

/** The text runs of a link's words (a link inside a link is its words). */
function flatRuns(runs: Inline[]): TextRun[] {
  return runs.flatMap((r) => (r.type === "link" ? r.content : [r]));
}

/** A block's words as the editor may hold them, made comparable: runs merged, styles cleaned. */
export function normalInline(content: unknown): Inline[] {
  if (!Array.isArray(content)) return [];
  const runs: Inline[] = [];
  for (const r of content as Array<Record<string, unknown>>) {
    if (r && r["type"] === "link" && typeof r["href"] === "string") {
      const inner = normalInline(r["content"]).flatMap((x) => (x.type === "text" ? [x] : x.content));
      runs.push({ type: "link", href: r["href"], content: inner.map((x) => ({ ...x, styles: linkStyles(x.styles) })) });
    } else if (r && typeof r["text"] === "string") {
      runs.push({ type: "text", text: r["text"], styles: clean((r["styles"] ?? {}) as Styles) });
    }
  }
  return mergeRuns(runs);
}

/** The words of a block's content, plain. */
export function plainText(content: unknown): string {
  return normalInline(content)
    .map((r) => (r.type === "text" ? r.text : r.content.map((c) => c.text).join("")))
    .join("");
}

// ── words: writing ──────────────────────────────────────────────────────────────────────────────────────────────

type Seg = { text: string; s: Styles; href?: string };

/** What a break inside the words becomes: a new line (paragraphs, lists, quotes) or <br> (a heading, a cell). */
type Breaks = "newline" | "br";

function escapeText(t: string, table: boolean): string {
  let out = "";
  for (let i = 0; i < t.length; i++) {
    const c = t[i]!;
    const prev = t[i - 1] ?? "";
    const next = t[i + 1] ?? "";
    if (c === "\\" || c === "*" || c === "`" || c === "[" || c === "]" || c === "~") out += `\\${c}`;
    else if (c === "_" && !(/[\p{L}\p{N}]/u.test(prev) && /[\p{L}\p{N}]/u.test(next))) out += "\\_";
    else if (c === "<" && /[A-Za-z/!?]/.test(next)) out += "\\<";
    else if (c === "|" && table) out += "\\|";
    else out += c;
  }
  return out;
}

function codeSpan(t: string): string {
  let longest = 0;
  for (const m of t.match(/`+/g) ?? []) longest = Math.max(longest, m.length);
  const fence = "`".repeat(longest + 1);
  const pad = t.startsWith("`") || t.endsWith("`") || (t.startsWith(" ") && t.endsWith(" ") && t.trim()) ? " " : "";
  return `${fence}${pad}${t}${pad}${fence}`;
}

/** How each style is opened and closed — Markdown's marks, or the tags when the marks will not read back. */
function marks(key: string, value: unknown, tags: boolean): [string, string] {
  switch (key) {
    case "bold":
      return tags ? ["<b>", "</b>"] : ["**", "**"];
    case "italic":
      return tags ? ["<i>", "</i>"] : ["*", "*"];
    case "strike":
      return tags ? ["<s>", "</s>"] : ["~~", "~~"];
    case "underline":
      return ["<u>", "</u>"];
    case "textColor":
      return [`<span color="${String(value)}">`, "</span>"];
    case "backgroundColor":
      return [`<span color="${String(value)}_bg">`, "</span>"];
    default:
      return ["", ""];
  }
}

/** Outermost first: a link around everything, then the colours and underline (tags), then the stars. */
const ORDER = ["href", "textColor", "backgroundColor", "underline", "strike", "bold", "italic"] as const;

function renderSegs(segs: Seg[], level: number, tags: boolean, ctx: { table: boolean; breaks: Breaks }): string {
  if (level >= ORDER.length) {
    return segs
      .map((g) => {
        const lines = g.text.split("\n");
        const words = lines.map((l) => (g.s["code"] ? (l ? codeSpan(l) : "") : escapeText(l, ctx.table)));
        return words.join(ctx.breaks === "br" ? "<br>" : "\n");
      })
      .join("");
  }
  const key = ORDER[level]!;
  const valueOf = (g: Seg) => (key === "href" ? g.href : g.s[key]);
  let out = "";
  for (let i = 0; i < segs.length; ) {
    const v = valueOf(segs[i]!);
    let j = i + 1;
    while (j < segs.length && valueOf(segs[j]!) === v) j++;
    const inner = renderSegs(segs.slice(i, j), level + 1, tags, ctx);
    if (v === undefined || v === false) out += inner;
    else if (key === "href") out += `[${inner}](${/[\s()<>]/.test(String(v)) ? `<${String(v)}>` : String(v)})`;
    else {
      const [a, b] = marks(key, v, tags);
      // Markdown's marks may not touch a space on their inside: the spaces go outside them.
      if (!tags && (key === "bold" || key === "italic" || key === "strike")) {
        const lead = /^\s*/.exec(inner)![0];
        const trail = /\s*$/.exec(inner.slice(lead.length))![0];
        const core = inner.slice(lead.length, inner.length - trail.length);
        out += core ? `${lead}${a}${core}${b}${trail}` : inner;
      } else out += `${a}${inner}${b}`;
    }
    i = j;
  }
  return out;
}

function segsOf(content: Inline[]): Seg[] {
  const out: Seg[] = [];
  for (const r of content) {
    if (r.type === "link") for (const c of r.content) out.push({ text: c.text, s: c.styles, href: r.href });
    else out.push({ text: r.text, s: r.styles });
  }
  return out;
}

const sameInline = (a: Inline[], b: Inline[]) => JSON.stringify(canon(a)) === JSON.stringify(canon(b));
const canon = (v: unknown): unknown =>
  Array.isArray(v)
    ? v.map(canon)
    : v && typeof v === "object"
      ? Object.fromEntries(
          Object.keys(v as object)
            .sort()
            .map((k) => [k, canon((v as Record<string, unknown>)[k])]),
        )
      : v;

/**
 * A block's words as Markdown. With Markdown's marks when they read back as the same runs; else with tags, which do.
 * In "newline" mode a block whose words hold an empty line (or start or end with a break) says its breaks as <br>,
 * since an empty line would end it.
 */
export function renderInline(content: unknown, ctx: { table?: boolean; breaks: Breaks }): string {
  const runs = normalInline(content);
  const c = { table: !!ctx.table, breaks: ctx.breaks };
  const text = runs.map((r) => (r.type === "text" ? r.text : r.content.map((x) => x.text).join(""))).join("");
  if (c.breaks === "newline" && (/\n\s*\n/.test(text) || /^\s*\n/.test(text) || /\n\s*$/.test(text) || /\n[ \t]/.test(text))) c.breaks = "br";
  const segs = segsOf(runs);
  const plain = renderSegs(segs, 0, false, c);
  if (sameInline(parseInline(plain), runs)) return plain;
  return renderSegs(segs, 0, true, c);
}

// ── blocks: writing ─────────────────────────────────────────────────────────────────────────────────────────────

const propsOf = (b: Block) => b.props ?? {};

function codeFence(text: string): string {
  let longest = 0;
  for (const m of text.match(/^ *`{3,}/gm) ?? []) longest = Math.max(longest, m.trim().length);
  return "`".repeat(Math.max(3, longest + 1));
}

/** A line the parser would read as the start of a block: it gets its first character escaped when it is words. */
function escapeLineStart(line: string): string {
  const m = /^(\s*)(.*)$/.exec(line)!;
  const t = m[2]!;
  if (/^(#{1,6})(\s|$)/.test(t) || /^[>+-](\s|$)/.test(t) || /^(-\s*){3,}$/.test(t) || /^(_\s*){3,}$/.test(t) || /^(\*\s*){3,}$/.test(t)) {
    return `${m[1]}\\${t}`;
  }
  const num = /^(\d{1,9})([.)])(\s|$)/.exec(t);
  if (num) return `${m[1]}${num[1]}\\${t.slice(num[1]!.length)}`;
  if (/^(`{3,}|~{3,})/.test(t)) return `${m[1]}\\${t}`;
  if (/^<\/?(toggle|p|empty-block)\b/i.test(t)) return `${m[1]}\\${t}`;
  if (/^\|/.test(t)) return `${m[1]}\\${t}`;
  return line;
}

/** Words over lines. Each line that would read as a block's start is escaped — the first too, unless a marker
 *  stands before it (a list item's). */
const wordsLines = (content: unknown, escapeFirst: boolean) =>
  renderInline(content, { breaks: "newline" })
    .split("\n")
    .map((l, i) => (i === 0 && !escapeFirst ? l : escapeLineStart(l)));

function tableLines(content: unknown): string[] {
  const t = (content ?? {}) as { rows?: Array<{ cells?: unknown[] }> };
  const rows = (t.rows ?? []).map((r) =>
    (r.cells ?? []).map((cell) => {
      const inline = cell && typeof cell === "object" && !Array.isArray(cell) ? (cell as { content?: unknown }).content : cell;
      return renderInline(inline, { table: true, breaks: "br" });
    }),
  );
  if (!rows.length) return ["|  |", "| --- |"];
  const cols = Math.max(1, ...rows.map((r) => r.length));
  const line = (cells: string[]) => `| ${Array.from({ length: cols }, (_, i) => cells[i] ?? "").join(" | ")} |`;
  return [line(rows[0]!), `| ${Array.from({ length: cols }, () => "---").join(" | ")} |`, ...rows.slice(1).map(line)];
}

/** The marker a list item starts with ("- ", "1. ", "- [ ] "); its children are indented by its width (bar the box). */
function listMarker(b: Block, n: number): string {
  if (b.type === "numberedListItem") return `${n}. `;
  if (b.type === "checkListItem") return propsOf(b)["checked"] === true ? "- [x] " : "- [ ] ";
  return "- ";
}
const childIndent = (b: Block, n: number) => (LIST.has(b.type) ? (b.type === "numberedListItem" ? `${n}. `.length : 2) : 4);

/** A toggle on one line, or — when it holds blocks — its opening tag and its title (renderBlocks adds the rest). */
function toggleLines(b: Block, hasChildren: boolean): string[] {
  if (!hasChildren) return [`${toggleOpen(b)}${renderInline(b.content, { breaks: "br" })}</toggle>`];
  return [toggleOpen(b), ...(normalInline(b.content).length ? wordsLines(b.content, true) : [])];
}

function isToggle(b: Block): boolean {
  return b.type === "toggleListItem" || (b.type === "heading" && propsOf(b)["isToggleable"] === true);
}
function toggleOpen(b: Block): string {
  return b.type === "heading" ? `<toggle heading="h${Number(propsOf(b)["level"] ?? 1)}">` : "<toggle>";
}

/**
 * A block's own lines — without its children, unindented. `n`: a numbered item's number. A toggle with children says
 * its opening tag and title here (renderBlocks adds what it holds, and the closing tag).
 */
export function renderSelf(b: Block, n = 1, hasChildren = b.children.length > 0): string[] {
  const p = propsOf(b);
  switch (b.type) {
    case "heading": {
      if (isToggle(b)) return toggleLines(b, hasChildren);
      const words = renderInline(b.content, { breaks: "br" });
      return [`${"#".repeat(Math.min(6, Math.max(1, Number(p["level"] ?? 1))))}${words ? ` ${words}` : ""}`];
    }
    case "toggleListItem":
      return toggleLines(b, hasChildren);
    case "quote": {
      const lines = wordsLines(b.content, true);
      return lines.map((l) => (l ? `> ${l}` : ">"));
    }
    case "bulletListItem":
    case "numberedListItem":
    case "checkListItem": {
      const marker = listMarker(b, n);
      const pad = " ".repeat(childIndent(b, n));
      const lines = wordsLines(b.content, false);
      // "- ---" is a divider, not an item.
      const first = DIVIDER.test(`${marker}${lines[0] ?? ""}`.trim()) ? `\\${lines[0]}` : (lines[0] ?? "");
      return [`${marker}${first}`.trimEnd(), ...lines.slice(1).map((l) => `${pad}${l}`)];
    }
    case "codeBlock": {
      const text = plainText(b.content);
      const fence = codeFence(text);
      const lang = typeof p["language"] === "string" && p["language"] !== "text" ? p["language"] : "";
      return [`${fence}${lang}`, ...(text ? text.split("\n") : []), fence];
    }
    case "table":
      return tableLines(b.content);
    case "divider":
      return ["---"];
    case "image": {
      const url = typeof p["url"] === "string" ? p["url"] : "";
      const caption = typeof p["caption"] === "string" ? p["caption"] : "";
      return [`![${escapeText(caption, false)}](${/[\s()<>]/.test(url) ? `<${url}>` : url})`];
    }
    default: {
      // A paragraph (and any block the dialect has no word for, said as its words).
      // Words that are only spaces would be an empty line, which is no block at all.
      if (!plainText(b.content).trim()) return ["<p />"];
      return wordsLines(b.content, true);
    }
  }
}

/** Siblings sit on consecutive lines when both are list items; any other two have an empty line between. */
const tight = (a: Block, b: Block) => LIST.has(a.type) && LIST.has(b.type);

/** The blocks as Markdown lines. */
export function renderBlocks(blocks: Block[]): string[] {
  const out: string[] = [];
  let n = 0;
  blocks.forEach((b, i) => {
    const prev = blocks[i - 1];
    if (prev && !tight(prev, b)) out.push("");
    n = b.type === "numberedListItem" ? (prev?.type === "numberedListItem" ? n + 1 : Number(propsOf(b)["start"] ?? 1) || 1) : 0;
    const self = renderSelf(b, n);
    out.push(...self);
    if (!b.children.length) return;
    const kids = renderBlocks(b.children);
    if (isToggle(b)) {
      out.push("", ...kids, "</toggle>");
      return;
    }
    const pad = " ".repeat(childIndent(b, n));
    // A list item's own list follows it directly; anything else under a block comes after an empty line.
    if (!(LIST.has(b.type) && LIST.has(b.children[0]!.type))) out.push("");
    out.push(...kids.map((l) => (l ? `${pad}${l}` : "")));
  });
  return out;
}

export function renderPage(blocks: Block[]): string {
  const lines = renderBlocks(blocks);
  return lines.length ? `${lines.join("\n")}\n` : "";
}

/** How a block's own lines are compared: trailing spaces and a numbered item's number do not count. */
export function keyOf(lines: string | string[]): string {
  const s = (Array.isArray(lines) ? lines : lines.split("\n")).map((l) => l.replace(/\s+$/, "")).join("\n");
  return s.replace(/\n+$/, "").replace(/^\d{1,9}[.)](\s|$)/, "1.$1");
}

// ── blocks: reading ─────────────────────────────────────────────────────────────────────────────────────────────

const blank = (l: string) => !l.trim();

function indentOf(line: string): number {
  let n = 0;
  for (const c of line) {
    if (c === " ") n++;
    else if (c === "\t") n += 4 - (n % 4);
    else break;
  }
  return n;
}

/** The line without up to `n` columns of its leading space. */
function dedent(line: string, n: number): string {
  let col = 0;
  let i = 0;
  while (i < line.length && col < n) {
    const c = line[i]!;
    if (c === " ") col++;
    else if (c === "\t") col += 4 - (col % 4);
    else break;
    i++;
  }
  return line.slice(i);
}

const HEADING = /^(#{1,6})(?:\s+(.*?))?(?:\s+#+)?\s*$/;
const BULLET = /^([-*+])(?:(\s+)(.*))?$/;
const NUMBER = /^(\d{1,9})([.)])(?:(\s+)(.*))?$/;
const FENCE = /^(`{3,}|~{3,})(.*)$/;
const DIVIDER = /^(?:(?:-[ \t]*){3,}|(?:_[ \t]*){3,}|(?:\*[ \t]*){3,})$/;
const TOGGLE = /^<toggle(?:\s+heading="h([1-6])")?\s*>(.*)$/i;
const EMPTY = /^<(?:p\s*\/|p>\s*<\/p|empty-block\s*\/?)>$/i;
const DELIM_ROW = /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;

function isTableAt(lines: string[], i: number): boolean {
  const a = lines[i]?.trim() ?? "";
  const b = lines[i + 1]?.trim() ?? "";
  return a.includes("|") && b.includes("|") && b.includes("-") && DELIM_ROW.test(b);
}

/** Whether this line (with the next, for a table) starts a block of its own — it then ends the words above it. */
function startsBlock(lines: string[], i: number): boolean {
  const t = lines[i]!.trim();
  return (
    HEADING.test(t) ||
    FENCE.test(t) ||
    t.startsWith(">") ||
    (BULLET.test(t) && (BULLET.exec(t)![2] !== undefined || t.length === 1)) ||
    (NUMBER.test(t) && (NUMBER.exec(t)![3] !== undefined || NUMBER.exec(t)![0] === t)) ||
    DIVIDER.test(t) ||
    TOGGLE.test(t) ||
    /^<\/toggle>/i.test(t) ||
    EMPTY.test(t) ||
    isTableAt(lines, i)
  );
}

/** A line of words: its leading space and its hard-break mark (a trailing backslash, or two spaces) gone. */
const wordLine = (l: string) => l.trim().replace(/\\$/, "");

function splitCells(row: string): string[] {
  let s = row.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|") && !s.endsWith("\\|")) s = s.slice(0, -1);
  const cells: string[] = [];
  let cur = "";
  for (let i = 0; i < s.length; i++) {
    if (s[i] === "\\" && s[i + 1] === "|") {
      cur += "\\|";
      i++;
      continue;
    }
    if (s[i] === "|") {
      cells.push(cur.trim());
      cur = "";
      continue;
    }
    cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
}

const CELL_PROPS = { colspan: 1, rowspan: 1, backgroundColor: "default", textColor: "default", textAlignment: "left" };

export type ParseOptions = {
  /** Pictures already on the page: an image line naming one of these is that picture; any other is a link. */
  imageUrls?: ReadonlySet<string>;
};

const inline = (text: string) => parseInline(text) as unknown[];

/** Markdown into blocks — each with the lines it was written as. */
export function parseMarkdown(md: string, opts: ParseOptions = {}): Parsed[] {
  const lines = md.replace(/\r\n?/g, "\n").split("\n");
  return parseLines(lines, opts);
}

function parseLines(lines: string[], opts: ParseOptions): Parsed[] {
  const out: Parsed[] = [];
  let i = 0;
  const rawOf = (from: number, to: number, ind: number) => lines.slice(from, to).map((l) => dedent(l, ind).replace(/\s+$/, "")).join("\n");

  while (i < lines.length) {
    if (blank(lines[i]!)) {
      i++;
      continue;
    }
    const ind = indentOf(lines[i]!);
    const t = lines[i]!.trim();
    const start = i;
    let block: Parsed;
    /** Where children may begin (a list item's: its words' column), or -1: this block holds none by indent. */
    let childMin = ind + 2;
    let selfEnd: number;

    const tog = TOGGLE.exec(t);
    const fence = FENCE.exec(t);
    const heading = HEADING.exec(t);
    const bullet = BULLET.exec(t);
    const number = NUMBER.exec(t);

    if (EMPTY.test(t)) {
      block = { type: "paragraph", content: [], children: [], raw: "" };
      i++;
    } else if (tog) {
      // A toggle: on one line, or a title and then what it holds, to its closing tag.
      const level = tog[1] ? Number(tog[1]) : 0;
      const kind = level ? { type: "heading", props: { ...(level !== 1 ? { level } : {}), isToggleable: true } } : { type: "toggleListItem" };
      const after = tog[2] ?? "";
      const oneLine = /^(.*)<\/toggle>\s*$/i.exec(after);
      if (oneLine) {
        block = { ...kind, content: inline(oneLine[1]!), children: [], raw: "" };
        i++;
      } else {
        const title: string[] = after.trim() ? [after.trim()] : [];
        let j = i + 1;
        while (j < lines.length && !blank(lines[j]!) && !/^\s*<\/toggle>\s*$/i.test(lines[j]!)) title.push(wordLine(lines[j++]!));
        selfEnd = j;
        let depth = 1;
        const inner: string[] = [];
        for (; j < lines.length; j++) {
          const l = lines[j]!.trim();
          if (TOGGLE.exec(l) && !/<\/toggle>\s*$/i.test(l)) depth++;
          if (/^<\/toggle>\s*$/i.test(l) && --depth === 0) break;
          inner.push(lines[j]!);
        }
        const innerInd = Math.min(...inner.filter((l) => !blank(l)).map(indentOf), Infinity);
        const children = parseLines(
          inner.map((l) => dedent(l, Number.isFinite(innerInd) ? innerInd : 0)),
          opts,
        );
        block = { ...kind, content: inline(title.join("\n")), children, raw: rawOf(start, selfEnd, ind) };
        i = Math.min(lines.length, j + 1);
        out.push(block);
        continue;
      }
    } else if (fence) {
      const mark = fence[1]!;
      const lang = (fence[2] ?? "").trim().split(/\s+/)[0] ?? "";
      const body: string[] = [];
      let j = i + 1;
      const close = new RegExp(`^${mark[0] === "`" ? "`" : "~"}{${mark.length},}\\s*$`);
      for (; j < lines.length; j++) {
        if (close.test(lines[j]!.trim()) && indentOf(lines[j]!) <= ind + 3) break;
        body.push(dedent(lines[j]!, ind));
      }
      const text = body.join("\n");
      block = {
        type: "codeBlock",
        ...(lang && lang !== "text" ? { props: { language: lang } } : {}),
        content: text ? [{ type: "text", text, styles: {} }] : [],
        children: [],
        raw: "",
      };
      i = Math.min(lines.length, j + 1);
    } else if (heading) {
      const level = heading[1]!.length;
      block = { type: "heading", ...(level !== 1 ? { props: { level } } : {}), content: inline(heading[2] ?? ""), children: [], raw: "" };
      i++;
    } else if (DIVIDER.test(t)) {
      block = { type: "divider", children: [], raw: "" };
      i++;
    } else if (isTableAt(lines, i)) {
      const head = splitCells(lines[i]!);
      const rows = [head];
      let j = i + 2;
      while (j < lines.length && !blank(lines[j]!) && lines[j]!.includes("|")) rows.push(splitCells(lines[j++]!));
      const cols = head.length;
      block = {
        type: "table",
        content: {
          type: "tableContent",
          columnWidths: Array.from({ length: cols }, () => null),
          headerRows: 1,
          rows: rows.map((r) => ({
            cells: Array.from({ length: cols }, (_, c) => ({ type: "tableCell", content: inline(r[c] ?? ""), props: { ...CELL_PROPS } })),
          })),
        },
        children: [],
        raw: "",
      };
      i = j;
    } else if (t.startsWith(">")) {
      const inner: string[] = [];
      let j = i;
      while (j < lines.length && lines[j]!.trim().startsWith(">")) inner.push(lines[j++]!.trim().replace(/^>\s?/, ""));
      const kids = parseLines(inner, opts);
      const first = kids[0];
      if (first && first.type === "paragraph") {
        block = { type: "quote", content: first.content ?? [], children: [...first.children, ...kids.slice(1)], raw: "" };
      } else block = { type: "quote", content: [], children: kids, raw: "" };
      i = j;
    } else if ((bullet && (bullet[2] !== undefined || t.length === 1)) || (number && (number[3] !== undefined || number[0] === t))) {
      // A list item: its marker, its words (with the lines that continue them), then what is indented under it.
      let words: string;
      let type: string;
      const props: Record<string, unknown> = {};
      let width: number;
      if (bullet) {
        const rest = bullet[3] ?? "";
        const box = /^\[( |x|X)?\](?:\s+(.*)|\s*)$/.exec(rest);
        width = 1 + (bullet[2]?.length ?? 1);
        if (box) {
          type = "checkListItem";
          if ((box[1] ?? "").toLowerCase() === "x") props["checked"] = true;
          words = box[2] ?? "";
        } else {
          type = "bulletListItem";
          words = rest;
        }
      } else {
        type = "numberedListItem";
        const n = Number(number![1]);
        const prev = out[out.length - 1];
        if (n !== 1 && prev?.type !== "numberedListItem") props["start"] = n;
        words = number![4] ?? "";
        width = number![1]!.length + 1 + (number![3]?.length ?? 1);
      }
      const text = [words.trim()];
      let j = i + 1;
      while (j < lines.length && !blank(lines[j]!) && !startsBlock(lines, j)) text.push(wordLine(lines[j++]!));
      text[0] = text[0]!.replace(/\\$/, "");
      block = { type, ...(Object.keys(props).length ? { props } : {}), content: inline(text.join("\n")), children: [], raw: "" };
      childMin = ind + Math.min(width, 2);
      i = j;
    } else {
      // Words: to an empty line, or a line that starts a block of its own.
      const text = [wordLine(lines[i]!)];
      let j = i + 1;
      while (j < lines.length && !blank(lines[j]!) && !startsBlock(lines, j)) text.push(wordLine(lines[j++]!));
      const joined = text.join("\n");
      const image = /^!\[([^\]]*)\]\((<[^>]*>|[^\s)]+)\)$/.exec(joined);
      const url = image ? image[2]!.replace(/^<|>$/g, "") : "";
      if (image && opts.imageUrls?.has(url)) {
        block = { type: "image", props: { url, ...(image[1] ? { caption: image[1] } : {}) }, children: [], raw: "" };
      } else block = { type: "paragraph", content: inline(joined), children: [], raw: "" };
      i = j;
    }
    selfEnd = i;
    block.raw = rawOf(start, selfEnd, ind);

    // What is indented under it is inside it.
    let j = i;
    let last = i;
    while (j < lines.length && (blank(lines[j]!) || indentOf(lines[j]!) >= childMin)) {
      if (!blank(lines[j]!)) last = j + 1;
      j++;
    }
    if (last > i) {
      const region = lines.slice(i, last);
      const least = Math.min(...region.filter((l) => !blank(l)).map(indentOf));
      block.children.push(...parseLines(region.map((l) => dedent(l, least)), opts));
      i = last;
    }
    out.push(block);
  }
  return out;
}
